import React, { useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';

/**
 * High order component (HOC) that wraps over VignetteCheck and ViolationCheck components.
 * Handles all requests and provides application data (inc. validation and error handling)
 */

interface State {
  data: any;
  error: string | null;
  loading: boolean;
}

export interface withDataCheckProps extends State {
  configureDataCheck: (data: any) => any;
}

const toUrlParams = (object: any) => {
  return Object.keys(object).reduce((v, a) => {
    return v + '/' + object[a];
  }, '');
};

const toUrlPath = (object: any) => {
  if (object.hasOwnProperty('delictId')) return '/delict';
  else if (object.hasOwnProperty('registrationNumber')) return '/plate';
  else if (object.hasOwnProperty('personalNumber')) return '/owner';
  else if (object.hasOwnProperty('referenceNo')) return '/reference';
  else return '';
};


const withDataCheck = (WrappedComponent: any) => () => {
  const initState = {
    data: null,
    error: null,
    loading: false
  };

  const [state, setState] = useState<any>(initState);

  const { t } = useTranslation();

  const configureDataCheck = (endpoint: string) => (dataToCheck: any) => { 
    let isReferenceDataValid = /^[0-9]+(\/[0-9]+)*$/.test(( dataToCheck.referenceNo || "").trim());
   
    //Checks if input data is valid
    for (let propName in dataToCheck) {
      if (dataToCheck.hasOwnProperty(propName) && !dataToCheck.hasOwnProperty('referenceNo')) {
        let isDataValid = /^[\u0400-\u04FFa-zA-Z0-9 ]+$/.test(dataToCheck[propName]);       
        if (!isDataValid) {
          setState({
            loading: false,
            error: null,
            data: { ok: false, status: { message: t('Моля, въведете коректни данни') } }
          });
          return;
        }       
      }
    }
    if (dataToCheck.hasOwnProperty('referenceNo') && !isReferenceDataValid) {
      setState({
        loading: false,
        error: null,
        data: { ok: false, status: { message: t('Моля, въведете коректни данни') } }
      });
      return;
    }

    if (dataToCheck.hasOwnProperty('delictId') && (dataToCheck.delictId.trim().length < 9 || dataToCheck.delictId.trim().length > 20)) {
      setState({
        loading: false,
        error: null,
        data: { ok: false, status: { message: t('Моля, въведете номер на нарушение') } }
      });
      return;
    }

    if (dataToCheck.hasOwnProperty('registrationNumber') && (dataToCheck.registrationNumber.trim().length < 2 || dataToCheck.registrationNumber.trim().length > 16)) {      
      setState({
        loading: false,
        error: null,
        data: { ok: false, status: { message: t('Моля, въведете регистрационен номер') } }
      });
      return;
    }

    if (dataToCheck.hasOwnProperty('personalNumber') && (dataToCheck.personalNumber.trim().length < 9 || dataToCheck.personalNumber.trim().length > 20)) {
      setState({
        loading: false,
        error: null,
        data: { ok: false, status: { message: t('Моля, въведете персонален номер') } }
      });
      return;
    }
    if (dataToCheck.hasOwnProperty('referenceNo') && (dataToCheck.referenceNo.trim().length < 9 || dataToCheck.referenceNo.trim().length > 20)) {
      setState({
        loading: false,
        error: null,
        data: { ok: false, status: { message: t('Моля, въведете референтен номер') } }
      });
      return;
    }

    if (dataToCheck.hasOwnProperty('referenceNo')) {
      axios
      .post(`/check/violation/delict/reference`, dataToCheck)
      .then((result: AxiosResponse<any>) => {
        setState({
          data: result.data,
          error: null,
          loading: false
        });
      })
      .catch((error: any) => {
        setState({
          data: { ok: false },
          error: error.response ? error.response : error,
          loading: false
        });
      });
    } else {
      axios
      .get(`${endpoint}${toUrlPath(dataToCheck)}${toUrlParams(dataToCheck)}`)
      .then((result: AxiosResponse<any>) => {
        setState({
          data: result.data,
          error: null,
          loading: false
        });
      })
      .catch((error: any) => {
        setState({
          data: { ok: false },
          error: error.response ? error.response : error,
          loading: false
        });
      });
  };
    }
  
  return <WrappedComponent configureDataCheck={configureDataCheck} {...state} />;
  
};

export default withDataCheck;
